import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@hooks/useAuth';
import { toast } from '@hooks/use-toast';
import {
  getVapidPublicKey,
  subscribeToPush,
  unsubscribeFromPush,
  getMyPushSubscriptions,
} from '@api-client/api-client';
import { urlBase64ToUint8Array, detectBrowser, isWebPushSupported } from '@utils/webPush';
import { logger } from '@utils/logger';
import { SW_MESSAGE } from '@lib/pwa/sw-messages';
import { invalidateChatCaches } from '@lib/chatCacheInvalidation';
import { useRotateStaleVapidSubscription } from '@hooks/useRotateStaleVapidSubscription';
import {
  useNotificationPermission,
  notifyPermissionChange,
} from '@hooks/useNotificationPermission';
import { useAuthStore } from '@stores/authStore';

// =============================================================================
// QUERY KEYS
// =============================================================================

const WEB_PUSH_KEYS = {
  subscription: ['web-push-subscription'] as const,
  vapidKey: ['web-push-vapid-key'] as const,
  backendSubscriptions: ['web-push-backend-subscriptions'] as const,
};

// =============================================================================
// RETURN TYPE
// =============================================================================

export interface UseWebPushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  isSubscribing: boolean;
  isUnsubscribing: boolean;
  /** Forwarded from `useRotateStaleVapidSubscription.rotationFailed` —
   *  see that hook for rationale, telemetry, and the InvalidStateError
   *  constraint. */
  isVapidRotationFailed: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

// =============================================================================
// HELPERS
// =============================================================================

const buildSubscriptionPayload = (subscription: PushSubscription) => {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) throw new Error('Missing encryption keys');
  return {
    endpoint: subscription.endpoint,
    p256dh_key: btoa(String.fromCharCode(...new Uint8Array(p256dh))),
    auth_key: btoa(String.fromCharCode(...new Uint8Array(auth))),
    browser: detectBrowser(),
  };
};

// =============================================================================
// HOOK
// =============================================================================

export const useWebPushNotifications = (): UseWebPushNotificationsReturn => {
  const { isAuthenticated, user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const isSupported = useMemo(() => isWebPushSupported(), []);

  // AIDEV-NOTE: the web counterpart of `nativePushTeardownGeneration` (#1173).
  // `unregisterWebPushSubscription` ends this browser's subscription at the
  // START of the push step, and the session stays valid until `clearAuthState`
  // up to the 5s ceiling later. Any refetch of the subscription query inside
  // that window now answers null, and `WebPushInitializer` auto-subscribes and
  // POSTs /push/subscribe as the LEAVING user; `create_subscription` upserts by
  // endpoint, so the signed-out browser keeps getting that account's pushes.
  // The triggers are coincidental but real: an SSE FULL_SYNC on reconnect and
  // the maintenance-recovery listener both run an unfiltered
  // `queryClient.invalidateQueries()`.
  //
  // Same latch as `useCurrentUser`'s, deliberately, rather than a second
  // sign-out concept: `executeSignOut` sets it before the push step, so it
  // covers this window and the whole run-up to it.
  const isSigningOut = useAuthStore(state => state.isSigningOut);

  // Track Notification.permission reactively (shared store — see
  // useNotificationPermission).
  const permission = useNotificationPermission();

  // Query the browser's current PushSubscription
  const {
    data: browserSubscription,
    isLoading: isSubLoading,
    dataUpdatedAt: browserSubscriptionUpdatedAt,
  } = useQuery({
    queryKey: WEB_PUSH_KEYS.subscription,
    queryFn: async (): Promise<PushSubscription | null> => {
      const registration = await navigator.serviceWorker.ready;
      return registration.pushManager.getSubscription();
    },
    enabled: isSupported && isAuthenticated && !isSigningOut,
    staleTime: Infinity,
  });

  // Cache the VAPID key. `refetchInterval` is the primary trigger: every
  // 30 minutes on focused tabs, paused on backgrounded tabs via
  // `refetchIntervalInBackground: false` (codebase pattern from
  // useMySpacesFeed and useChat — don't burn battery polling). The matching
  // `staleTime` is what makes the focus/reconnect refetch options behave
  // like a small assist rather than the safety net: they're gated by
  // staleness, so a tab that returns within 30 min of the last fetch waits
  // for the interval too. Worst-case detection latency is ~30 min on
  // focused tabs; rotations happen on the order of months, so plenty fresh.
  // Rotation itself is detected by useRotateStaleVapidSubscription once the
  // queries reconcile.
  const { data: vapidKeyData, isLoading: isVapidLoading } = useQuery({
    queryKey: WEB_PUSH_KEYS.vapidKey,
    queryFn: async () => {
      const response = await getVapidPublicKey();
      return response.data.public_key;
    },
    enabled: isSupported && isAuthenticated,
    staleTime: 1000 * 60 * 30,
    refetchInterval: 1000 * 60 * 30,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Resync: fetch backend subscriptions to compare with browser subscription
  const { data: backendSubscriptions } = useQuery({
    queryKey: WEB_PUSH_KEYS.backendSubscriptions,
    queryFn: async () => {
      const response = await getMyPushSubscriptions();
      return response.data;
    },
    enabled: isSupported && isAuthenticated && !!browserSubscription,
    gcTime: 0, // Fresh check each mount
  });

  // Resync mutation: re-POST orphaned browser subscription to backend
  const { mutate: resyncMutate } = useMutation({
    mutationFn: async (subscription: PushSubscription) => {
      // Live read, not the render-time binding: a mutation already dispatched
      // when sign-out starts would otherwise re-POST the leaving account's
      // subscription. See the latch note at the top of this hook.
      if (useAuthStore.getState().isSigningOut) return;
      return subscribeToPush(buildSubscriptionPayload(subscription));
    },
    onError: error => {
      logger.warn('Web push: resync failed (non-critical):', error);
    },
  });

  // Detect VAPID key mismatch and self-heal stale browser subscriptions.
  // See useRotateStaleVapidSubscription for the full rationale, multi-tab
  // behavior, failure handling, and per-session attempts cap.
  const { isStale: isVapidKeyStale, rotationFailed: isVapidRotationFailed } =
    useRotateStaleVapidSubscription({
      browserSubscription,
      vapidKey: vapidKeyData,
      subscriptionQueryKey: WEB_PUSH_KEYS.subscription,
      backendSubscriptionsQueryKey: WEB_PUSH_KEYS.backendSubscriptions,
      subscriptionDataUpdatedAt: browserSubscriptionUpdatedAt,
    });

  // Compare browser subscription against backend — trigger resync if orphaned.
  // null = query not loaded; empty Set = loaded with no subscriptions (orphan detected).
  // Skip resync if VAPID key is stale — the rotation hook will handle cleanup.
  const browserEndpoint = browserSubscription?.endpoint ?? null;
  const backendEndpoints = useMemo(
    () => (backendSubscriptions ? new Set(backendSubscriptions.map(s => s.endpoint)) : null),
    [backendSubscriptions],
  );

  useEffect(() => {
    if (!browserEndpoint || backendEndpoints == null || !browserSubscription || isVapidKeyStale)
      return;
    if (!backendEndpoints.has(browserEndpoint)) {
      logger.info('Web push: resyncing orphaned browser subscription to backend');
      resyncMutate(browserSubscription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- browserSubscription tracked via browserEndpoint primitive
  }, [browserEndpoint, backendEndpoints, resyncMutate, isVapidKeyStale]);

  // Listen for service worker messages (subscription changes, chat notifications)
  useEffect(() => {
    if (!isSupported || !isAuthenticated) return;

    const handleMessage = (event: MessageEvent) => {
      const payload = event.data as { type?: string; data?: Record<string, unknown> } | undefined;
      if (payload?.type === SW_MESSAGE.PUSH_SUBSCRIPTION_CHANGED) {
        logger.info('Web push: subscription changed event from SW, invalidating');
        void queryClient.invalidateQueries({ queryKey: WEB_PUSH_KEYS.subscription });
      } else if (payload?.type === SW_MESSAGE.CHAT_MESSAGE_RECEIVED) {
        logger.info('Web push: chat message received in active chat, invalidating caches');
        // Shared helper so this branch and the OneSignal-path consumers refresh
        // the exact same chat caches (never rejects; errors are logged inside).
        void invalidateChatCaches(queryClient, userId);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [isSupported, isAuthenticated, queryClient, userId]);

  // Subscribe mutation: PushManager subscribe + backend POST + rollback on failure
  const subscribeMutation = useMutation({
    mutationFn: async ({ vapidKey }: { vapidKey: string }) => {
      // See the latch note at the top of this hook. Checked live, because
      // `enabled` cannot stop a mutation and the auto-subscribe effect may
      // already have fired when sign-out begins.
      if (useAuthStore.getState().isSigningOut) return;
      const registration = await navigator.serviceWorker.ready;
      let subscription: PushSubscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      } catch (error) {
        logger.error('Web push: pushManager.subscribe() failed:', error);
        throw error;
      }

      // Checked again: `pushManager.subscribe()` is a network round trip to the
      // push service, and sign-out can begin inside it. End the new
      // subscription rather than leave the signed-out browser holding one.
      if (useAuthStore.getState().isSigningOut) {
        try {
          await subscription.unsubscribe();
        } catch (rollbackError) {
          logger.warn(
            'Web push: could not end a subscription created during sign-out:',
            rollbackError,
          );
        }
        return;
      }

      let payload;
      try {
        payload = buildSubscriptionPayload(subscription);
      } catch {
        try {
          await subscription.unsubscribe();
        } catch (rollbackError) {
          logger.warn('Web push: rollback unsubscribe also failed:', rollbackError);
        }
        throw new Error('Subscription missing encryption keys');
      }

      try {
        await subscribeToPush(payload);
      } catch (backendError) {
        logger.error('Web push: backend subscribe failed, rolling back:', backendError);
        try {
          await subscription.unsubscribe();
        } catch (rollbackError) {
          logger.warn('Web push: rollback unsubscribe also failed:', rollbackError);
        }
        throw backendError;
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: WEB_PUSH_KEYS.subscription });
    },
  });
  const { mutateAsync: subscribeMutateAsync } = subscribeMutation;

  // Subscribe wrapper: permission request (browser UI, only when needed) + toast on failure
  const subscribe = useCallback(async (): Promise<void> => {
    if (!isSupported) return;

    // Only prompt when we don't already hold permission. On Chrome/Firefox a
    // repeat Notification.requestPermission() while already 'granted' is a
    // harmless no-op, but Safari throws "Notification prompting can only be
    // done from a user gesture" on ANY call made outside a user gesture — and
    // the returning-user auto-subscribe path (LegacyWebPushInitializer) calls
    // subscribe() from an effect, not a gesture. Skipping the prompt when
    // permission is already granted keeps that path silent and gesture-free.
    if (Notification.permission !== 'granted') {
      const result = await Notification.requestPermission();
      notifyPermissionChange();

      if (result !== 'granted') {
        logger.info('Web push: permission not granted');
        return;
      }
    }

    if (!vapidKeyData) {
      logger.error('Web push: VAPID key not available');
      toast({
        title: 'Failed to enable notifications',
        description: 'Please try again.',
        variant: 'error',
      });
      return;
    }

    try {
      await subscribeMutateAsync({ vapidKey: vapidKeyData });
    } catch {
      toast({
        title: 'Failed to enable notifications',
        description: 'Please try again.',
        variant: 'error',
      });
    }
  }, [isSupported, vapidKeyData, subscribeMutateAsync]);

  // Unsubscribe mutation: backend DELETE + browser PushSubscription.unsubscribe
  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      try {
        await unsubscribeFromPush({ endpoint: subscription.endpoint });
      } catch (error) {
        logger.error('Web push: backend unsubscribe failed:', error);
        throw error; // Preserve browser subscription if backend record was not removed
      }

      await subscription.unsubscribe();
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: WEB_PUSH_KEYS.subscription });
    },
  });
  const { mutateAsync: unsubscribeMutateAsync } = unsubscribeMutation;

  // Unsubscribe wrapper: toast on failure
  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!isSupported) return;
    try {
      await unsubscribeMutateAsync();
    } catch {
      toast({
        title: 'Failed to disable notifications',
        description: 'Please try again.',
        variant: 'error',
      });
    }
  }, [isSupported, unsubscribeMutateAsync]);

  return {
    isSupported,
    permission,
    isSubscribed: !!browserSubscription,
    isLoading: isSubLoading || isVapidLoading,
    isSubscribing: subscribeMutation.isPending,
    isUnsubscribing: unsubscribeMutation.isPending,
    isVapidRotationFailed,
    subscribe,
    unsubscribe,
  };
};
